/**
 * components/ui/primitives.tsx
 * Small shared building blocks used across feature modules so the
 * LKC look-and-feel stays consistent. Kept intentionally minimal.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'gold';

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-primary text-white hover:bg-primaryDark shadow-sm shadow-primary/30',
  secondary:
    'bg-white text-primary border border-primary/30 hover:bg-primary/5 dark:bg-gray-800 dark:text-gray-100 dark:border-gray-600',
  ghost:
    'bg-transparent text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700/50',
  danger: 'bg-red-600 text-white hover:bg-red-700 shadow-sm shadow-red-600/30',
  gold: 'bg-secondary text-ink hover:bg-accent shadow-sm shadow-secondary/30',
};

interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'primary',
  loading = false,
  icon,
  children,
  className = '',
  disabled,
  ...rest
}: BtnProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed ${VARIANTS[variant]} ${className}`}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
}

export function Card({
  children,
  className = '',
  glass = false,
}: {
  children: ReactNode;
  className?: string;
  glass?: boolean;
}) {
  const base = glass
    ? 'rounded-2xl border bg-white/95 border-white/50 shadow-2xl ring-1 ring-black/5 backdrop-blur-xl dark:bg-gray-900/85 dark:border-white/10'
    : 'rounded-2xl border bg-white border-gray-100 shadow-sm hover:shadow-md transition-shadow dark:bg-gray-800 dark:border-gray-700';
  return (
    <div className={`${base} ${className}`}>
      {children}
    </div>
  );
}

export function Badge({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${className}`}
    >
      {children}
    </span>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 gap-3">
      <div className="relative" style={{ width: 64, height: 64 }}>
        <div
          className="absolute inset-0 rounded-full animate-spin"
          style={{
            border: '4px solid transparent',
            borderTopColor: '#1b3a6b',
            borderRightColor: '#d4a92c',
          }}
        />
        <div className="absolute flex items-center justify-center" style={{ inset: 8 }}>
          <img
            src="/images/lkc_logo.png"
            alt=""
            className="w-10 h-10 rounded-full object-cover"
            onError={(e) => {
              const t = e.target as HTMLImageElement;
              t.style.display = 'none';
              t.parentElement!.innerHTML = '<span style="font-weight:bold;color:#1b3a6b;font-size:14px">LKC</span>';
            }}
          />
        </div>
      </div>
      {label && (
        <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">{label}</p>
      )}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {title}
        </h1>
        {subtitle && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}
